// File: /src/pages/UserAccount.js

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';

import { cpf, cnpj } from 'cpf-cnpj-validator';
import PersonalDataForm from '../components/PersonalDataForm';
import PasswordUpdateForm from '../components/PasswordUpdateForm';
import FinancialDataForm from '../components/FinancialDataForm';
import SettingsSection from '../components/SettingsSection';
import { useTheme } from '../contexts/ThemeContext';
import '../styles/UserAccount.css';
import apiClient from '../services/apiClient';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { useAuth } from '../hooks/useAuth';

function UserAccount() {
const { isDarkMode, toggleTheme } = useTheme();
const { refreshUser } = useAuth();
	const [userData, setUserData] = useState({
		id: '',
		name: '',
		email: '',
		cpf_cnpj: '',
		birthOrFoundationDate: null,
		gender: '',
		phone: '',
		address: '',
		currentPassword: '',
		password: '',
		confirmPassword: '',
		pixKey: '',
		cep: '',
		number: '',
		city: '',
		state: '',
		complement: '',
	});
	// Novo estado para armazenar o valor digitado no campo Pix (temporário)
	const [pixKeyInput, setPixKeyInput] = useState('');
	const [loading, setLoading] = useState(true);
	const [cpfLocked, setCpfLocked] = useState(false);
	const [passwordError, setPasswordError] = useState('');
	const [emailError, setEmailError] = useState('');

	// Funções auxiliares
	const tryFormat = (rawValue) => {
		const onlyDigits = rawValue.replace(/\D/g, '');
		if (cpf.isValid(onlyDigits)) return cpf.format(onlyDigits);
		if (cnpj.isValid(onlyDigits)) return cnpj.format(onlyDigits);
		return onlyDigits;
	};

	const detectType = (rawValue) => {
		const onlyDigits = rawValue.replace(/\D/g, '');
		if (cpf.isValid(onlyDigits)) return 'pf';
		if (cnpj.isValid(onlyDigits)) return 'pj';
		return 'none';
	};

	const validatePassword = (password) => {
		const regex =
			/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
		return regex.test(password);
	};

	const validateEmail = (email) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	// Atualiza o estado para inputs; para o campo pixKey usamos o estado pixKeyInput
	const handleChange = (e) => {
		const { name, value } = e.target;
		if (name === 'cpf_cnpj') {
			const formatted = tryFormat(value);
			setUserData((prev) => ({
				...prev,
				cpf_cnpj: formatted,
			}));
		} else if (name === 'pixKey') {
			setPixKeyInput(value);
		} else {
			setUserData((prev) => ({
				...prev,
				[name]: value,
			}));
		}

		if (name === 'email') {
			setEmailError(validateEmail(value) ? '' : 'E-mail inválido.');
		}

		if (name === 'password') {
			setPasswordError(
				validatePassword(value)
					? ''
					: 'A senha deve conter pelo menos 8 caracteres, incluindo 1 letra maiúscula, 1 letra minúscula, 1 número e 1 caractere especial.'
			);
		}

		if (name === 'confirmPassword' || name === 'password') {
			setPasswordError(
				userData.password &&
					userData.confirmPassword &&
					userData.password !== userData.confirmPassword
					? 'As senhas não coincidem.'
					: ''
			);
		}
	};

	const handleDateChange = (date) => {
		setUserData((prev) => ({
			...prev,
			birthOrFoundationDate: date,
		}));
	};

	// Busca os dados do usuário e atualiza os estados
	const fetchUserData = useCallback(async () => {
		try {
			const res = await apiClient.get(
				API_ENDPOINTS.USERS_ME,
				
			);
			if (res.data) {
				setUserData((prev) => ({
					...prev,
					id: res.data.id || '',
					name: res.data.name || '',
					email: res.data.email || '',
					cpf_cnpj: res.data.cpf_cnpj
						? tryFormat(res.data.cpf_cnpj)
						: '',
					birthOrFoundationDate: res.data.birthOrFoundationDate
						? new Date(res.data.birthOrFoundationDate)
						: null,
					gender: res.data.gender || '',
					phone: res.data.phone || '',
					address: res.data.address || '',
					pixKey: res.data.pixKey || '',
					cep: res.data.cep || '',
					number: res.data.number || '',
					city: res.data.city || '',
					state: res.data.state || '',
					complement: res.data.complement || '',
				}));
				// Também atualiza o valor do campo de input com o valor persistido
				setPixKeyInput(res.data.pixKey || '');
				if (res.data.cpf_cnpj) {
					setCpfLocked(true);
				}
			}
		} catch (error) {
			console.error('❌ Erro ao buscar dados do usuário:', error);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchUserData();
	}, [fetchUserData]);

	// Envia os dados pessoais (exceto senha e informações financeiras)
	const handlePersonalSubmit = async (e) => {
		e.preventDefault();
		if (!validateEmail(userData.email)) {
			toast.error('Por favor, insira um e-mail válido.');
			return;
		}
		if (!userData.id) {
			toast.error('Erro: ID do usuário não encontrado.');
			return;
		}

		// Removida a linha que tirava a formatação do CPF/CNPJ
		let sendData = {
			name: userData.name,
			email: userData.email,
			cpf_cnpj: userData.cpf_cnpj,
			birthOrFoundationDate:
				detectType(userData.cpf_cnpj) === 'pj'
					? null
					: userData.birthOrFoundationDate,
			gender: userData.gender,
			phone: userData.phone,
			address: userData.address,
			cep: userData.cep || null,
			number: userData.number,
			city: userData.city,
			state: userData.state,
			complement: userData.complement,
		};

		try {
			await apiClient.put(
				API_ENDPOINTS.userById(userData.id),
				sendData,
				
			);
			await refreshUser();
			setCpfLocked(true);
			toast.success('Dados atualizados com sucesso!');
		} catch (error) {
			console.error('❌ Erro ao atualizar dados do usuário:', error);
			const errorMessage =
				error.response?.data?.error || 'Erro ao atualizar dados.';
			toast.error(errorMessage);
		}
	};

	// Envia a atualização de senha
	const handlePasswordSubmit = async (e) => {
		e.preventDefault();

		setPasswordError('');

		if (!userData.currentPassword) {
			toast.error('Por favor, insira sua senha atual.');
			return;
		}
		if (!userData.password) {
			toast.error('Por favor, insira uma nova senha.');
			return;
		}
		if (!validatePassword(userData.password)) {
			toast.error(
				'A senha deve conter pelo menos 8 caracteres, incluindo 1 letra maiúscula, 1 letra minúscula, 1 número e 1 caractere especial.'
			);
			return;
		}
		if (userData.password !== userData.confirmPassword) {
			toast.error('As senhas não coincidem.');
			return;
		}
		if (!userData.id) {
			toast.error('Erro: ID do usuário não encontrado.');
			return;
		}

		let sendData = {
			cpf_cnpj: userData.cpf_cnpj, // Removido o .replace(/\D/g, '')
			currentPassword: userData.currentPassword,
			password: userData.password,
		};

		try {
			await apiClient.put(
				API_ENDPOINTS.userById(userData.id),
				sendData,
				
			);
			await refreshUser();
			toast.success('Senha atualizada com sucesso!');
			setUserData((prev) => ({
				...prev,
				currentPassword: '',
				password: '',
				confirmPassword: '',
			}));
		} catch (error) {
			console.error('❌ Erro ao atualizar a senha:', error);
			const errorMessage =
				error.response?.data?.error || 'Erro ao atualizar senha.';
			toast.error(errorMessage);
		}
	};

	// Função para atualizar a chave Pix (informações financeiras)
	const handleFinancialSubmit = async (e) => {
		e.preventDefault();
		if (!pixKeyInput) {
			toast.error('Por favor, insira sua chave Pix.');
			return;
		}
		try {
			await apiClient.put(
				API_ENDPOINTS.USERS_PIX,
				{ pixKey: pixKeyInput },
				
			);
			await refreshUser();
			toast.success('Chave Pix atualizada com sucesso!');
			// Após a atualização, refaz a busca para atualizar a chave exibida na tela
			await fetchUserData();
		} catch (error) {
			console.error('Erro ao atualizar a chave Pix:', error);
			const errorMessage =
				error.response?.data?.error || 'Erro ao atualizar a chave Pix.';
			toast.error(errorMessage);
		}
	};

	if (loading)
		return (
			<p className="loading-text">
				Carregando...
				<br />
				<span className="loading-spinner"></span>
			</p>
		);

	return (
		<div className="user-account">
			<header>
				<h1>Minha Conta</h1>
			</header>
			<SettingsSection
				isDarkMode={isDarkMode}
				toggleTheme={toggleTheme}
			/>
			<PersonalDataForm
				userData={userData}
				cpfLocked={cpfLocked}
				emailError={emailError}
				handleChange={handleChange}
				handleDateChange={handleDateChange}
				handlePersonalSubmit={handlePersonalSubmit}
				detectType={detectType}
			/>
			<PasswordUpdateForm
				userData={userData}
				passwordError={passwordError}
				handleChange={handleChange}
				handlePasswordSubmit={handlePasswordSubmit}
			/>
			<FinancialDataForm
				userData={userData}
				pixKeyInput={pixKeyInput}
				setPixKeyInput={setPixKeyInput}
				handleFinancialSubmit={handleFinancialSubmit}
			/>			
		</div>
	);
}

export default UserAccount;
